import type { Position } from "./types";

export type TutorialStep = "SWAP" | "ROTATE" | "GROOVE" | "RADIAL";

export interface TutorialState {
  active: boolean;
  step: TutorialStep;
  stepIndex: number;
  highlightCells: Position[];
  highlightSwap: { from: Position; to: Position } | null;
  /** UI rect keys to highlight with a golden pulse (e.g. "rotateLeft", "grooveMeter"). */
  highlightRects: string[];
  textLines: string[];
  waitingForAction: boolean;
  elapsed: number;
  autoAdvanceAfter: number;
}

/**
 * Fixed 10×10 board for the tutorial (color indices 0-2, level-1 palette).
 * Swapping (5,4)↔(5,5) creates a horizontal 3-match at row 5 cols 2,3,4.
 * Verified: no pre-existing 3-in-a-row matches, at least one valid move.
 *
 * Row 5 before swap: ... 0, 0, 2, 0, ...  (cols 2-5)
 * Row 5 after  swap: ... 0, 0, 0, 2, ...  → cols 2,3,4 = 0,0,0 match ✓
 */
export const TUTORIAL_BOARD: number[][] = [
  [0, 2, 1, 0, 2, 1, 0, 2, 1, 0],
  [1, 0, 2, 1, 0, 2, 1, 0, 2, 1],
  [2, 1, 0, 2, 1, 0, 2, 1, 0, 2],
  [0, 2, 1, 0, 2, 1, 0, 2, 1, 0],
  [1, 0, 2, 1, 0, 2, 1, 0, 2, 1],
  [2, 1, 0, 0, 2, 0, 2, 1, 0, 2],
  [0, 2, 1, 2, 1, 1, 0, 2, 1, 0],
  [1, 0, 2, 1, 0, 1, 1, 0, 2, 1],
  [2, 1, 0, 2, 1, 0, 2, 1, 0, 2],
  [0, 2, 1, 0, 2, 1, 0, 2, 1, 0]
];

interface StepDef {
  step: TutorialStep;
  textLines: string[];
  highlightCells: Position[];
  highlightSwap: { from: Position; to: Position } | null;
  highlightRects: string[];
  waitingForAction: boolean;
  autoAdvanceAfter: number;
}

const STEPS: StepDef[] = [
  {
    step: "SWAP",
    textLines: ["DRAG two tiles to swap", "and match 3 or more!"],
    highlightCells: [
      { row: 5, col: 3 },
      { row: 5, col: 4 },
      { row: 5, col: 5 }
    ],
    highlightSwap: { from: { row: 5, col: 4 }, to: { row: 5, col: 5 } },
    highlightRects: [],
    waitingForAction: true,
    autoAdvanceAfter: 999
  },
  {
    step: "ROTATE",
    textLines: ["TAP the turntables to", "rotate the board!"],
    highlightCells: [],
    highlightSwap: null,
    highlightRects: ["rotateLeft", "rotateRight"],
    waitingForAction: true,
    autoAdvanceAfter: 10
  },
  {
    step: "GROOVE",
    textLines: ["Fill the GROOVE meter", "then TAP it to activate!"],
    highlightCells: [],
    highlightSwap: null,
    highlightRects: ["grooveMeter"],
    waitingForAction: true,
    autoAdvanceAfter: 8
  },
  {
    step: "RADIAL",
    textLines: ["HOLD your character to", "use POWERUPS and TRAPS!"],
    highlightCells: [],
    highlightSwap: null,
    highlightRects: ["characterHub"],
    waitingForAction: true,
    autoAdvanceAfter: 8
  }
];

export function createTutorialState(): TutorialState {
  const first = STEPS[0];
  return {
    active: true,
    step: first.step,
    stepIndex: 0,
    highlightCells: [...first.highlightCells],
    highlightSwap: first.highlightSwap,
    highlightRects: [...first.highlightRects],
    textLines: [...first.textLines],
    waitingForAction: first.waitingForAction,
    elapsed: 0,
    autoAdvanceAfter: first.autoAdvanceAfter
  };
}

export function advanceTutorialStep(state: TutorialState): boolean {
  const nextIndex = state.stepIndex + 1;
  if (nextIndex >= STEPS.length) {
    state.active = false;
    return false;
  }
  const next = STEPS[nextIndex];
  state.stepIndex = nextIndex;
  state.step = next.step;
  state.highlightCells = [...next.highlightCells];
  state.highlightSwap = next.highlightSwap;
  state.highlightRects = [...next.highlightRects];
  state.textLines = [...next.textLines];
  state.waitingForAction = next.waitingForAction;
  state.elapsed = 0;
  state.autoAdvanceAfter = next.autoAdvanceAfter;
  return true;
}

export function updateTutorial(state: TutorialState, dt: number): void {
  if (!state.active) return;
  state.elapsed += dt;
  if (state.elapsed >= state.autoAdvanceAfter) {
    advanceTutorialStep(state);
  }
}

export function isTutorialComplete(state: TutorialState): boolean {
  return !state.active;
}

/** Called when the player makes any swap. */
export function notifyTutorialSwap(state: TutorialState): void {
  if (state.active && state.step === "SWAP" && state.waitingForAction) {
    advanceTutorialStep(state);
  }
}

/** Called when the player performs a rotate or groove action. */
export function notifyTutorialAction(state: TutorialState, step: TutorialStep): void {
  if (state.active && state.step === step && state.waitingForAction) {
    advanceTutorialStep(state);
  }
}

/** Called when the player holds the character hub long enough for radial. */
export function notifyTutorialRadialHold(state: TutorialState): void {
  if (state.active && state.step === "RADIAL" && state.waitingForAction) {
    advanceTutorialStep(state);
  }
}
